import { useQuery } from '@apollo/client';
import { AppRegistration } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useUpdateTemplate } from '~/api/templates';
import { RouterTabs } from '~/components/RouterTabs';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import {
  TemplateLayoutDocument,
  type TemplateLayout__Template as Template,
} from '../../queries/layout.generated';
import { Actions } from './actions';
import { Menu } from './menu';

export interface LayoutProps {
  children: ReactNode;
  noPadding?: boolean;
}

export const Layout = ({ children, noPadding = false }: LayoutProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const params = useParams<{ templateId: string }>();
  const templateId = Number(params.templateId);

  const [moreMenuProps, moreActionProps, moreTableActions] = useMoreActions<Template>();
  const [updateTemplate] = useUpdateTemplate();

  const { data, loading } = useQuery(TemplateLayoutDocument, { variables: { templateId } });
  const template = data?.template;

  if (!loading && template == null) return <Navigate to={link('/not-found')} replace />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {template && (
        <>
          <Toolbar
            pageTitle={template.name}
            titleIcon={<AppRegistration />}
            actions={<Actions onShowMenu={moreTableActions(template).onClick} />}
            titleText={!template.canUpdate.value && template.name}
            titleEditor={
              template.canUpdate.value && (
                <InlineEditTitle
                  inputId={`templates-app-item-${template.id}-name`}
                  value={template.name}
                  update={(value) =>
                    updateTemplate({ variables: { templateId, patch: { name: value.trim() } } })
                  }
                  tooltip={t('pages.templates.layout.edit_tooltip')}
                  validationSchema={object({
                    name: string().required(t('pages.templates.layout.name_required_error')).trim(),
                  })}
                />
              )
            }
          />
          <Menu
            template={template}
            moreMenuProps={moreMenuProps}
            moreActionProps={moreActionProps}
          />
          <nav>
            <RouterTabs
              tabs={[
                { label: t('common.labels.settings'), to: '../settings' },
                { label: t('pages.templates.layout.configuration_tab'), to: '../configuration' },
                { label: t('pages.templates.layout.usage_tab'), to: '../usage' },
                { label: t('pages.templates.layout.versions_tab'), to: '../versions' },
              ]}
              variant="scrollable"
            />
          </nav>
          <PageContainer padding={!noPadding}>{children}</PageContainer>
        </>
      )}
    </LoadingPane>
  );
};
